import React, { useState, useEffect, useRef } from 'react';
import { Clock, AlertCircle, AlertTriangle, CheckCircle, Loader } from 'lucide-react';
import { GAME_DURATION, hasKanji, toHiragana, API_URL } from '../utils/gameUtils';

export default function GameScreen({ topic, onFinish, history, setHistory }) {
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [inputVal, setInputVal] = useState('');
  const [readingVal, setReadingVal] = useState(''); // 読み仮名用
  const [errorMsg, setErrorMsg] = useState('');
  const [warningMsg, setWarningMsg] = useState('');
  const [rejectedApplications, setRejectedApplications] = useState([]);
  const [pendingLevel3Words, setPendingLevel3Words] = useState([]);

  const [bonusEffect, setBonusEffect] = useState(null);
  const inputRef = useRef(null);

  const isComposing = useRef(false);

  // タイマー
  useEffect(() => {
    if (timeLeft <= 0) {
      // 終了時に重複を除去してから親に渡す (念のため)
      const uniqueRejected = Array.from(
        new Map(rejectedApplications.map(item => [item.word, item])).values()
      );
      onFinish(history, uniqueRejected, pendingLevel3Words);
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft, onFinish, history, rejectedApplications, pendingLevel3Words]);

  // フォーカス
  useEffect(() => {
    inputRef.current?.focus();
  }, [history]);

  const handleCompositionStart = () => {
    isComposing.current = true;
  };

  const handleCompositionEnd = () => {
    isComposing.current = false;
  };

  const handleReadingCompositionEnd = (e) => {
    isComposing.current = false;
    const val = e.target.value;
    // ひらがなと長音符以外を除去
    const validVal = val.split('').filter(char => {
      return /[\u3040-\u309F\u30FC]/.test(char);
    }).join('');
    setReadingVal(validVal);
  };

  const handleReadingChange = (e) => {
    const val = e.target.value;

    if (isComposing.current) {
      setReadingVal(val);
      return;
    }

    // ひらがなと長音符以外を除去
    const validVal = val.split('').filter(char => {
      return /[\u3040-\u309F\u30FC]/.test(char);
    }).join('');
    setReadingVal(validVal);
  };

  // 入力ハンドラ
  const handleWordChange = (e) => {
    const val = e.target.value;
    setInputVal(val);

    // 漢字が含まれていない場合、自動的に読み仮名にもひらがな変換後の値を入力
    if (!hasKanji(val)) {
      // モバイル等のIMEで変換中に一時的に空文字になる現象（フリガナ消失）を防ぐ
      if (val === '' && isComposing.current) {
        return;
      }
      const converted = toHiragana(val);
      const validReading = converted.split('').filter(char => /[\u3040-\u309F\u30FC]/.test(char)).join('');
      setReadingVal(validReading);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const rawWord = inputVal.trim();
    let rawReading = readingVal.trim(); // modifyable

    // 0. 空文字チェック (単語のみ)
    if (!rawWord) {
      setErrorMsg('単語を入力してね！');
      return;
    }

    // 読み仮名入力がある場合の先行チェック
    // ★修正: 漢字が含まれる場合はフロントの読み仮名（自動入力）が途中入力の可能性があるため、信用せずにバックエンドに任せる
    let normalizedReading = '';


    if (rawReading) {
      normalizedReading = toHiragana(rawReading);

      // 1. 重複チェック (表記)
      if (history.includes(rawWord)) {
        setErrorMsg('その単語は回答済みです！');
        setWarningMsg('');
        return;
      }
      // 2. 「ん」チェック (読み)
      if (normalizedReading.endsWith('ん')) {
        setErrorMsg('「ん」で終わっています！');
        setWarningMsg('');
        return;
      }
      // 3. 頭文字チェック (読み)
      if (normalizedReading.charAt(0) !== topic) {
        setErrorMsg(`「${topic}」から始まる単語を入れてね！`);
        setWarningMsg('');
        return;
      }

      // 4. スパム/無意味な文字列チェック (厳格化)
      // 同じ文字が4回以上続く (例: ああああ)
      if (/(.)\1{3,}/.test(normalizedReading)) {
        setErrorMsg('同じ文字が続きすぎています！');
        setWarningMsg('');
        return;
      }

      // 連番チェック (例: あいう)
      let isSequential = false;
      if (normalizedReading.length >= 3) {
        for (let i = 0; i < normalizedReading.length - 2; i++) {
          const c1 = normalizedReading.charCodeAt(i);
          const c2 = normalizedReading.charCodeAt(i + 1);
          const c3 = normalizedReading.charCodeAt(i + 2);
          // ひらがな/カタカナ/英数字の連番を弾く
          if (c2 - c1 === 1 && c3 - c2 === 1) {
            isSequential = true;
            break;
          }
        }
      }
      if (isSequential) {
        setErrorMsg('意味のない文字列はダメだよ！');
        setWarningMsg('');
        return;
      }
    }

    // 4. バックエンド辞書判定 & 読み仮名補完 (Level2までの即時判定)
    try {
      const res = await fetch(`${API_URL}/validate-word-quick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ word: rawWord })
      });
      if (!res.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await res.json();
      console.log("Validation result:", data);

      if (!data.valid) {
        const r = data.reason || '';
        if (r.includes('Empty')) {
          setErrorMsg('単語を入力してください');
        } else if (r.includes('Unknown') || r.includes('OOV') || r.includes('Web辞書') || r.includes('辞書')) {
          setErrorMsg('その言葉は辞書にありませんでした');
        } else if (r.includes('Not a noun')) {
          setErrorMsg('名詞を入力してください');
        } else {
          // バックエンドからの日本語メッセージがあればそれを表示
          setErrorMsg(r || '無効な単語です');
        }

        // 申請可能（辞書/名詞NG）な場合、リストに追加
        if (r.includes('Unknown') || r.includes('OOV') || r.includes('Web辞書') || r.includes('辞書') || r.includes('名詞') || (r && r.includes('判定不能'))) {
          // 既にリストになければ追加
          // ステート更新時の重複を防ぐため、関数型アップデート内でチェック
          setRejectedApplications(prev => {
            if (prev.some(item => item.word === rawWord)) {
              return prev;
            }
            return [...prev, {
              word: rawWord,
              reading: readingVal || data.reading || rawWord, // attempt to get reading
              reason: r
            }];
          });
        }
        return;
      }

      // 読み仮名がない場合のみ、バックエンドの値を採用
      if (!rawReading) {
        rawReading = data.reading;
        if (!rawReading || rawReading === '*') {
          setErrorMsg('読み仮名を入力してください');
          return;
        }
        normalizedReading = toHiragana(rawReading);

        // 遅延チェック: 読みが確定したのでここでルール判定
        // 1. 重複チェック
        if (history.includes(rawWord)) {
          setErrorMsg('その単語は回答済みです！');
          setWarningMsg('');
          return;
        }
        // 2. 「ん」チェック
        if (normalizedReading.endsWith('ん')) {
          setErrorMsg('「ん」で終わっています！');
          setWarningMsg('');
          return;
        }
        // 3. 頭文字チェック
        if (normalizedReading.charAt(0) !== topic) {
          setErrorMsg(`「${topic}」から始まる単語を入れてね！（読み: ${normalizedReading}）`);
          setWarningMsg('');
          return;
        }

        // 4. スパム/無意味な文字列チェック (厳格化) - 自動取得読み仮名に対しても実施
        if (/(.)\1{3,}/.test(normalizedReading)) {
          setErrorMsg('同じ文字が続きすぎています！');
          setWarningMsg('');
          return;
        }

        // 連番チェック
        let isSequential = false;
        if (normalizedReading.length >= 3) {
          for (let i = 0; i < normalizedReading.length - 2; i++) {
            const c1 = normalizedReading.charCodeAt(i);
            const c2 = normalizedReading.charCodeAt(i + 1);
            const c3 = normalizedReading.charCodeAt(i + 2);
            if (c2 - c1 === 1 && c3 - c2 === 1) {
              isSequential = true;
              break;
            }
          }
        }
        if (isSequential) {
          setErrorMsg('意味のない文字列はダメだよ！');
          setWarningMsg('');
          return;
        }
      }

      // 成功！
      // Level3判定待ちの場合、pending リストに追加
      if (data.pending_level3) {
        setPendingLevel3Words(prev => [...prev, rawWord]);
      }

      // ボーナス判定 (アニメーション用)
      const len = rawWord.length;
      if (len >= 6) {
        const bonusPoints = len >= 8 ? 2 : 1;
        setBonusEffect({ points: bonusPoints });
        // アニメーション終了後に消す
        setTimeout(() => setBonusEffect(null), 2000);
      }

      setHistory([rawWord, ...history]);
      setInputVal('');
      setReadingVal('');
      setErrorMsg('');
      setWarningMsg('');

    } catch (error) {
      console.error("Validation error:", error);
      setErrorMsg('サーバー判定エラー: 通信を確認してください');
      return;
    }

  };

  return (
    <div className="space-y-6">
      {/* 情報表示エリア */}
      <div className="flex justify-between items-center bg-gray-100 p-3 rounded-lg border border-gray-200">
        <div className="text-center">
          <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">TOPIC</p>
          <p className="text-4xl font-black text-orange-600 bg-white w-14 h-14 flex items-center justify-center rounded-xl shadow-sm mx-auto border-2 border-orange-100">
            {topic}
          </p>
        </div>
        <div className="text-center w-2/3 pl-4">
          <div className="flex flex-col items-center w-full">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="text-2xl font-mono font-bold text-gray-700 leading-none">
                {timeLeft}
              </span>
              <span className="text-xs text-gray-400 pt-1">/ {GAME_DURATION}</span>
            </div>
            {/* プログレスバー */}
            <div className="w-full bg-gray-200 h-3 rounded-full overflow-hidden shadow-inner">
              <div
                className={`h-full transition-all duration-1000 ease-linear ${timeLeft <= 5 ? 'bg-red-500 animate-pulse' : 'bg-orange-500'
                  }`}
                style={{ width: `${(timeLeft / GAME_DURATION) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 入力フォーム */}
      <form onSubmit={handleSubmit} className="space-y-3 relative">
        <div className="space-y-1">
          <input
            ref={inputRef}
            type="text"
            value={inputVal}
            onChange={handleWordChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            placeholder={`「${topic}」から始まる単語 (漢字OK)`}
            className="w-full border-2 border-orange-200 rounded-xl p-3 text-lg focus:outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-100 transition-all pl-4 shadow-sm"
            autoFocus
          />
          {/* 読み仮名入力欄 */}
          <div className="relative">
            <input
              type="text"
              value={readingVal}
              onChange={handleReadingChange}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleReadingCompositionEnd}
              placeholder="ふりがな (ひらがな)"
              className="w-full bg-gray-50 border border-gray-200 rounded-lg p-2 text-sm text-gray-600 focus:outline-none focus:border-orange-400 focus:bg-white transition-all pl-4"
            />
            <span className="absolute right-3 top-2 text-xs text-gray-400 pointer-events-none">よみ</span>
          </div>
        </div>

        <button
          type="submit"
          disabled={!inputVal || !readingVal}
          onMouseDown={(e) => e.preventDefault()}
          className="w-full bg-orange-500 text-white rounded-lg py-3 font-bold disabled:bg-gray-300 disabled:cursor-not-allowed hover:bg-orange-600 active:scale-95 transition-all shadow-md flex items-center justify-center gap-2"
        >
          Enter⏎
        </button>
      </form>

      {/* メッセージエリア */}
      <div className="h-auto min-h-[24px] flex flex-col items-center justify-center gap-1">
        {errorMsg && (
          <div className="text-red-500 text-sm flex items-center gap-1 font-bold animate-shake">
            <AlertCircle className="w-4 h-4" />
            {errorMsg}
          </div>
        )}

        {warningMsg && !errorMsg && (
          <div className="text-amber-500 text-xs flex items-center gap-1 animate-fade-in">
            <AlertTriangle className="w-3 h-3" />
            {warningMsg}
          </div>
        )}
      </div>

      {/* 履歴リスト */}
      <div className="bg-gray-50 rounded-xl p-4 h-[180px] overflow-y-auto border border-gray-200 shadow-inner">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-xs text-gray-400 font-bold uppercase tracking-wider">回答履歴</h3>
          <span className="bg-orange-100 text-orange-600 text-xs font-bold px-2 py-0.5 rounded-full">
            {history.length}
          </span>
        </div>
        <ul className="flex flex-col-reverse gap-2">
          {history.map((word, index) => {
            const isPending = pendingLevel3Words.includes(word);
            return (
              <li key={index} className={`px-3 py-2 rounded-lg text-sm shadow-sm border flex items-center justify-between animate-slide-in-right ${isPending ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100'}`}>
                <span className="font-medium text-gray-700">{word}</span>
                {isPending ? (
                  <span className="flex items-center gap-1 text-amber-500">
                    <Loader className="w-3 h-3 animate-spin" />
                    <span className="text-[10px] font-bold">判定中</span>
                  </span>
                ) : (
                  <CheckCircle className="w-4 h-4 text-green-500 opacity-50" />
                )}
              </li>
            );
          })}
          {history.length === 0 && (
            <li className="text-gray-400 text-sm italic w-full text-center py-8 opacity-50">
              まだ回答がありません<br />入力してGo!
            </li>
          )}
        </ul>
      </div>


      {/* シンプルでモダンなボーナスアニメーション */}
      {bonusEffect && (
        <div className="fixed top-24 left-0 right-0 pointer-events-none flex justify-center z-50">
          <div className="bg-white/90 backdrop-blur text-gray-900 font-semibold text-2xl px-6 py-3 rounded-xl shadow-lg animate-[fadeUp_0.6s_ease-out_forwards]">
            +{bonusEffect.points} pt
          </div>
        </div>
      )}
      {/* Tailwind用カスタムアニメーション */}
      <style jsx>{`@keyframes fadeUp {from {opacity: 0;transform: translateY(12px) scale(0.96);} to {opacity: 1;transform: translateY(0) scale(1);}}`}</style>
    </div>
  );
}
